/**
 * Runtime configuration for the SPA.
 *
 * Values are read from Vite env vars at build time. In dev, sensible
 * defaults are provided so `npm run dev` works without a full Cognito
 * setup. In prod, CDK injects the real values at build time via
 * `VITE_*` env vars referencing the stack outputs (task 20.6).
 */

/** Backend REST API base URL (e.g. `https://abc.execute-api.us-east-1.amazonaws.com/prod`). */
export const API_ENDPOINT: string =
  (import.meta.env.VITE_API_ENDPOINT as string | undefined) ??
  "http://localhost:3001";

/** Cognito user pool id (e.g. `us-east-1_abc123`). */
export const USER_POOL_ID: string =
  (import.meta.env.VITE_USER_POOL_ID as string | undefined) ?? "";

/** Cognito SPA app client id. */
export const USER_POOL_CLIENT_ID: string =
  (import.meta.env.VITE_USER_POOL_CLIENT_ID as string | undefined) ?? "";

/**
 * Cognito hosted UI base URL
 * (e.g. `https://agent-gov-demo-123.auth.us-east-1.amazoncognito.com`).
 */
export const HOSTED_UI_DOMAIN: string =
  (import.meta.env.VITE_HOSTED_UI_DOMAIN as string | undefined) ?? "";

/** AWS region; defaults to the only supported region per design.md. */
export const AWS_REGION: string =
  (import.meta.env.VITE_AWS_REGION as string | undefined) ?? "us-east-1";

/**
 * Real_Registry MCP_Endpoint URL, baked into the SPA bundle at build
 * time from the stack output `RegistryMcpEndpoint` (tasks 3.2, 3.5,
 * 13.9). Shown verbatim on the Admin "About governance" page so
 * presenters can copy it into Kiro or Claude Code as an MCP client
 * target. Empty in local dev when the stack has not been deployed.
 */
export const REGISTRY_MCP_ENDPOINT: string =
  (import.meta.env.VITE_REGISTRY_MCP_ENDPOINT as string | undefined) ?? "";

/**
 * AWS Agent Registry id this demo seeds (via `scripts/seed-agent-registry.mjs`).
 * Used by the Admin UI to deep-link into the AWS Console so presenters
 * can show the real Registry + approval workflow.
 */
export const AGENT_REGISTRY_ID: string =
  (import.meta.env.VITE_AGENT_REGISTRY_ID as string | undefined) ?? "";

/** Console URL for the seeded Agent Registry. Empty when id is unset. */
export const AGENT_REGISTRY_CONSOLE_URL: string = AGENT_REGISTRY_ID
  ? `https://${AWS_REGION}.console.aws.amazon.com/agent-registry/home?region=${AWS_REGION}#/registry/${AGENT_REGISTRY_ID}`
  : "";

/** Deployment stage: `"dev"` or `"prod"`. Defaults to `"dev"` in local dev. */
export const STAGE: "dev" | "prod" =
  ((import.meta.env.VITE_STAGE as string | undefined) ?? "dev") as "dev" | "prod";

/**
 * Hostname → Stage mapping, per R13.1 ("hostname-based Stage detection")
 * and design.md §"9.1 SPA — hostname-based Stage detection".
 *
 * Exactly one prod hostname; everything else (dev hostname, localhost,
 * vite preview, CloudFront raw URL) resolves to `"dev"`. Keeping the
 * default at `"dev"` ensures local development never accidentally
 * targets prod.
 */
export function resolveStageFromHostname(hostname?: string): "dev" | "prod" {
  const h =
    hostname ??
    (typeof globalThis !== "undefined" && globalThis.location?.hostname
      ? globalThis.location.hostname
      : "localhost");
  if (h === "agentmesh.brentstr.com") return "prod";
  // agentmesh.dev.brentstr.com, localhost, vite preview, raw CF URLs
  // all fall through to dev.
  return "dev";
}

/**
 * Resolved Stage for the running SPA, computed once at module load
 * from `window.location.hostname` via {@link resolveStageFromHostname}.
 *
 * Use this for hostname-driven Stage logic (R13.1). The legacy
 * `STAGE` constant remains env-var driven and is kept for backward
 * compatibility with components that already import it.
 */
export const stage: "dev" | "prod" = resolveStageFromHostname();

/**
 * Expected AWS account id per Stage (R13.6). The SPA asserts that
 * every composite envelope's `expectedAccountId` matches the value
 * for the SPA's resolved Stage; on mismatch the payload is discarded
 * (R13.5).
 */
export const EXPECTED_ACCOUNT_ID_BY_STAGE = {
  dev: "875409651890",
  prod: "860315495423",
} as const;

/**
 * Returns the expected AWS account id for the given Stage, defaulting
 * to the SPA's resolved current Stage. Consolidates the hostname →
 * account-id mapping that previously lived inside
 * `pages/agent-inventory/useInventoryData.ts`.
 */
export function resolveExpectedAccountId(
  s: "dev" | "prod" = stage,
): string {
  return EXPECTED_ACCOUNT_ID_BY_STAGE[s];
}

/**
 * True when the SPA has enough config to start the OAuth2 code flow. In
 * dev without config we render a friendly "login not wired" notice
 * instead of redirecting to a broken URL.
 */
export function isAuthConfigured(): boolean {
  return (
    USER_POOL_CLIENT_ID.length > 0 && HOSTED_UI_DOMAIN.length > 0
  );
}
